import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { Logger } from '@services/logging.service';
import { azure } from '../../environments/environment.prod';

/** Channel name for cross-tab logout (BroadcastChannel). */
const LOGOUT_CHANNEL = 'xm_auth_channel';
/** Key used for cross-tab logout fallback (localStorage storage event). */
const LOGOUT_SIGNAL_KEY = 'xm_logout_signal';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
  private logoutChannel: BroadcastChannel | null = null;
  private storageListener: ((e: StorageEvent) => void) | null = null;

  constructor(private msalService: MsalService, private zone: NgZone) {
    this.initCrossTabLogout();
  }

  /** Redirect to Microsoft login page */
  login(): void {
    const currentRoute = window.location.hash;
    if (currentRoute && currentRoute !== '#/') {
      localStorage.setItem('appRoute', currentRoute);
    }
    Logger.debug('[AuthService] Initiating MSAL login redirect');
    this.msalService.loginRedirect({
      scopes: ['openid', azure.scope],
    });
  }

  logout(): void {
    Logger.debug('[AuthService] Initiating MSAL logout');
    this.broadcastLogout();
    this.msalService.logoutRedirect();
  }

  reAuthOnError(): void {
    Logger.debug('[AuthService] reAuthOnError — triggering MSAL login');
    this.login();
  }

  /** Send logout signal to all other tabs. */
  private broadcastLogout(): void {
    if (this.logoutChannel) {
      // Modern browsers: BroadcastChannel
      this.logoutChannel.postMessage({ type: 'logout' });
    } else {
      // Fallback: write a throwaway key to localStorage to trigger storage event in other tabs
      localStorage.setItem(LOGOUT_SIGNAL_KEY, Date.now().toString());
      localStorage.removeItem(LOGOUT_SIGNAL_KEY);
    }
  }

  /** Handle logout signal received from another tab. */
  private onLogoutReceived(): void {
    Logger.debug('[AuthService] Logout detected from another tab — performing MSAL logout');
    this.zone.run(() => {
      this.msalService.logoutRedirect();
    });
  }

  /**
   * Sets up cross-tab logout listener.
   * Uses BroadcastChannel when available, falls back to localStorage storage event.
   */
  private initCrossTabLogout(): void {
    if (typeof BroadcastChannel !== 'undefined') {
      // Modern browsers
      this.logoutChannel = new BroadcastChannel(LOGOUT_CHANNEL);
      this.logoutChannel.onmessage = (event) => {
        if (event.data?.type === 'logout') {
          this.onLogoutReceived();
        }
      };
    } else {
      // Fallback for older browsers (IE11, old Safari, old Edge)
      this.storageListener = (event: StorageEvent) => {
        if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
          this.onLogoutReceived();
        }
      };
      window.addEventListener('storage', this.storageListener);
    }
  }

  ngOnDestroy(): void {
    this.logoutChannel?.close();
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
  }
}
